import { Injectable, OnDestroy } from '@angular/core';

export type AudioTestStatus = 'idle' | 'passed' | 'failed' | 'warning';

export interface AudioRecordingTestResult {
    status: AudioTestStatus;
    message: string;
    inputDeviceName: string;
    outputDeviceName: string;
    microphonePermission: string;
    recordingStatus: string;
    playbackStatus: string;
    browserSupport: string;
}

export interface AudioDeviceList {
    audioInputDevices: MediaDeviceInfo[];
    audioOutputDevices: MediaDeviceInfo[];
    selectedInputDeviceId: string;
    selectedOutputDeviceId: string;
}

@Injectable({
    providedIn: 'root'
})
export class AudioDeviceTestService implements OnDestroy {
    private mediaRecorder?: MediaRecorder;
    private audioChunks: Blob[] = [];
    private activeAudioStream?: MediaStream;
    private recordingStopResolver?: () => void;
    private recordedAudioUrl = '';

    ngOnDestroy(): void {
        this.cleanup();
    }

    async loadAudioDevices(
        selectedInputDeviceId = '',
        selectedOutputDeviceId = ''
    ): Promise<AudioDeviceList> {
        if (!navigator.mediaDevices?.enumerateDevices) {
            throw new Error('Audio device listing is not supported.');
        }

        const permissionStream = await navigator.mediaDevices.getUserMedia({
            audio: true
        });

        permissionStream.getTracks().forEach(track => track.stop());

        const devices = await navigator.mediaDevices.enumerateDevices();

        const audioInputDevices = devices.filter(
            device => device.kind === 'audioinput' && device.deviceId !== 'communications'
        );

        const audioOutputDevices = devices.filter(
            device => device.kind === 'audiooutput' && device.deviceId !== 'communications'
        );

        return {
            audioInputDevices,
            audioOutputDevices,
            selectedInputDeviceId:
                selectedInputDeviceId || audioInputDevices[0]?.deviceId || '',
            selectedOutputDeviceId:
                selectedOutputDeviceId || audioOutputDevices[0]?.deviceId || ''
        };
    }

    async runAudioRecordingTest(
        selectedInputDeviceId: string,
        selectedOutputDeviceId: string,
        audioInputDevices: MediaDeviceInfo[],
        audioOutputDevices: MediaDeviceInfo[],
        onRecordingStarted?: (result: AudioRecordingTestResult) => void,
        recordingDurationMs = 5000
    ): Promise<{
        result: AudioRecordingTestResult;
        recordedAudioUrl: string;
    }> {
        this.clearPreviousRecording();
        this.stopActiveAudioStream();

        const inputDevice = audioInputDevices.find(
            device => device.deviceId === selectedInputDeviceId && device.deviceId !== "communications"
        );

        const outputDevice = audioOutputDevices.find(
            device => device.deviceId === selectedOutputDeviceId && device.deviceId !== "communications"
        );

        const inputName = inputDevice?.label || 'Selected microphone';
        const outputName = outputDevice?.label || 'Selected speaker';

        try {
            const constraints: MediaStreamConstraints = {
                audio: selectedInputDeviceId
                    ? {
                        deviceId: {
                            exact: selectedInputDeviceId
                        }
                    }
                    : true
            };

            this.activeAudioStream = await navigator.mediaDevices.getUserMedia(
                constraints
            );

            this.audioChunks = [];

            const mimeType = this.getSupportedMimeType();

            this.mediaRecorder = mimeType
                ? new MediaRecorder(this.activeAudioStream, { mimeType })
                : new MediaRecorder(this.activeAudioStream);

            this.mediaRecorder.ondataavailable = event => {
                if (event.data && event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            };

            const recordingStoppedPromise = new Promise<void>(resolve => {
                this.recordingStopResolver = resolve;

                if (this.mediaRecorder) {
                    this.mediaRecorder.onstop = () => {
                        resolve();
                    };
                }
            });

            this.mediaRecorder.start();

            onRecordingStarted?.({
                status: 'warning',
                message: 'Recording your microphone audio. Please speak now...',
                inputDeviceName: inputName,
                outputDeviceName: outputName,
                microphonePermission: 'Allowed',
                recordingStatus: 'Recording...',
                playbackStatus: 'Waiting',
                browserSupport: 'Recording supported'
            });

            await this.delay(recordingDurationMs);

            if (this.mediaRecorder?.state === 'recording') {
                this.mediaRecorder.stop();
            }

            await recordingStoppedPromise;

            const audioBlob = new Blob(this.audioChunks, {
                type: mimeType || 'audio/webm'
            });

            if (!audioBlob.size) {
                return {
                    recordedAudioUrl: '',
                    result: {
                        status: 'failed',
                        message: 'Recording failed. No audio data was captured.',
                        inputDeviceName: inputName,
                        outputDeviceName: outputName,
                        microphonePermission: 'Allowed',
                        recordingStatus: 'No audio captured',
                        playbackStatus: 'Not played',
                        browserSupport: 'Recording failed'
                    }
                };
            }

            this.recordedAudioUrl = URL.createObjectURL(audioBlob);

            const playbackResult = await this.playRecordedAudio(
                this.recordedAudioUrl,
                selectedOutputDeviceId
            );

            return {
                recordedAudioUrl: this.recordedAudioUrl,
                result: {
                    status: playbackResult.status,
                    message: playbackResult.message,
                    inputDeviceName: inputName,
                    outputDeviceName: outputName,
                    microphonePermission: 'Allowed',
                    recordingStatus: 'Recorded successfully',
                    playbackStatus: playbackResult.playbackStatus,
                    browserSupport: playbackResult.browserSupport
                }
            };
        } catch {
            return {
                recordedAudioUrl: '',
                result: {
                    status: 'failed',
                    message:
                        'Audio recording test failed. Please check microphone permission and device availability.',
                    inputDeviceName: inputName,
                    outputDeviceName: outputName,
                    microphonePermission: 'Denied / Failed',
                    recordingStatus: 'Failed',
                    playbackStatus: 'Failed',
                    browserSupport: 'Browser or permission issue'
                }
            };
        } finally {
            this.stopActiveAudioStream();
        }
    }

    stopRecording(): void {
        if (this.mediaRecorder?.state === 'recording') {
            this.mediaRecorder.stop();
        }

        if (this.recordingStopResolver) {
            this.recordingStopResolver();
            this.recordingStopResolver = undefined;
        }
    }

    cleanup(): void {
        if (this.mediaRecorder?.state === 'recording') {
            this.mediaRecorder.stop();
        }

        this.stopActiveAudioStream();
        this.clearPreviousRecording();
    }

    private async playRecordedAudio(
        audioUrl: string,
        selectedOutputDeviceId: string
    ): Promise<{
        status: 'passed' | 'warning';
        message: string;
        playbackStatus: string;
        browserSupport: string;
    }> {
        const audioElement = new Audio(audioUrl);

        const typedAudioElement = audioElement as HTMLMediaElement & {
            setSinkId?: (sinkId: string) => Promise<void>;
        };

        const supportsSinkId = typeof typedAudioElement.setSinkId === 'function';

        if (supportsSinkId && selectedOutputDeviceId) {
            try {
                await typedAudioElement.setSinkId!(selectedOutputDeviceId);
            } catch {
                await audioElement.play();

                return {
                    status: 'warning',
                    message:
                        'Recording completed and played, but selected speaker could not be assigned.',
                    playbackStatus: 'Played in browser default output',
                    browserSupport:
                        'setSinkId failed. Browser blocked selected speaker assignment.'
                };
            }
        }

        await audioElement.play();

        return {
            status: supportsSinkId ? 'passed' : 'warning',
            message: supportsSinkId
                ? 'Recording completed and played through the selected output device.'
                : 'Recording completed and played through browser default output device.',
            playbackStatus: supportsSinkId
                ? 'Played in selected output device'
                : 'Played in default output device',
            browserSupport: supportsSinkId
                ? 'Speaker selection supported'
                : 'Speaker selection not supported in this browser'
        };
    }

    private getSupportedMimeType(): string {
        const mimeTypes = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/ogg;codecs=opus',
            'audio/mp4'
        ];

        for (const type of mimeTypes) {
            if (MediaRecorder.isTypeSupported(type)) {
                return type;
            }
        }

        return '';
    }

    private stopActiveAudioStream(): void {
        if (this.activeAudioStream) {
            this.activeAudioStream.getTracks().forEach(track => track.stop());
            this.activeAudioStream = undefined;
        }
    }

    private clearPreviousRecording(): void {
        if (this.recordedAudioUrl) {
            URL.revokeObjectURL(this.recordedAudioUrl);
        }

        this.recordedAudioUrl = '';
        this.audioChunks = [];
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    public resetaudiodevice() {
        this.clearPreviousRecording();
        this.stopActiveAudioStream();
    }
}